import React, { useState, useEffect } from 'react';
import { 
  Smartphone, 
  CreditCard, 
  MapPin, 
  Clock, 
  Zap, 
  Shield, 
  Users, 
  TrendingUp,
  ChevronRight,
  CheckCircle,
  Globe,
  Database,
  Cpu,
  Leaf
} from 'lucide-react';

const ExpertiseBillettique = () => {
  const [activeTab, setActiveTab] = useState('billettique');
  const [animateCards, setAnimateCards] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setAnimateCards(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const expertiseAreas = {
    billettique: {
      title: "Billettique Complète",
      icon: <CreditCard className="w-8 h-8" />,
      color: "from-blue-600 to-purple-600",
      features: [
        "Écosystème multi-canal (Web, Mobile, Back-office)",
        "Gestion complète Badgéo & e-badgéo",
        "Serveur de Rechargement Billettique sécurisé",
        "Cycle de vie complet des supports",
        "Validation mobile & QR codes",
        "Intégration TPV & personnalisation"
      ]
    },
    information: {
      title: "Information Voyageurs",
      icon: <MapPin className="w-8 h-8" />,
      color: "from-green-600 to-blue-600",
      features: [
        "Temps réel & horaires théoriques",
        "Recherche d'itinéraires multimodale",
        "Géolocalisation & points d'intérêt",
        "Gestion des perturbations",
        "Services complémentaires intégrés",
        "Favoris & personnalisation"
      ]
    },
    technique: {
      title: "Architecture Moderne",
      icon: <Cpu className="w-8 h-8" />,
      color: "from-purple-600 to-pink-600",
      features: [
        "Micro-services sécurisés",
        "Technologies serverless",
        "Conteneurisation avancée",
        "Intégrations partenaires",
        "Solutions responsives",
        "Optimisation énergétique"
      ]
    },
    innovation: {
      title: "Innovation & MaaS",
      icon: <Zap className="w-8 h-8" />,
      color: "from-orange-600 to-red-600",
      features: [
        "Mobility as a Service (MaaS)",
        "IA/RAD reconnaissance documents",
        "Refonte Flutter éco-responsable",
        "Vision long terme",
        "Amélioration continue",
        "Transition écologique"
      ]
    }
  };

  const stats = [
    { label: "Lignes de transport", value: "51", icon: <TrendingUp className="w-6 h-6" /> },
    { label: "Années d'expérience", value: "10+", icon: <Clock className="w-6 h-6" /> },
    { label: "Modules développés", value: "57", icon: <Database className="w-6 h-6" /> },
    { label: "Réduction problèmes", value: "80%", icon: <CheckCircle className="w-6 h-6" /> }
  ];

  const technologies = [
    "Flutter", "React", "Node.js", "Docker", "Kubernetes", 
    "OpenCMS", "Microservices", "API REST", "CI/CD", "Cloud"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">O</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">ORMAES</h1>
                <p className="text-sm text-gray-600">Expertise Billettique & Transport</p>
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-6">
              <span className="text-sm text-gray-600">Strasbourg • CTS Partner</span>
              <div className="flex items-center space-x-2 text-green-600">
                <Leaf className="w-4 h-4" />
                <span className="text-sm font-medium">Numérique Responsable</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-5xl font-bold text-gray-900 mb-6">
            L'expertise qui fait
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> rouler </span>
            vos projets
          </h2>
          <p className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto">
            Partenaire technologique de la Compagnie des Transports Strasbourgeois, nous maîtrisons l'écosystème complet 
            de la billettique moderne et de l'information voyageurs temps réel.
          </p>
          
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
            {stats.map((stat, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="flex items-center justify-center mb-4">
                  <div className="p-3 bg-gradient-to-br from-blue-100 to-purple-100 rounded-lg">
                    {stat.icon}
                  </div>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Expertise Tabs */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">Nos Expertises</h3>
            <p className="text-lg text-gray-600">
              Une maîtrise complète de l'écosystème transport public
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="flex flex-wrap justify-center mb-12 gap-4">
            {Object.entries(expertiseAreas).map(([key, area]) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex items-center space-x-3 px-6 py-3 rounded-full transition-all duration-300 ${
                  activeTab === key
                    ? 'bg-gradient-to-r ' + area.color + ' text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {area.icon}
                <span className="font-medium">{area.title}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-8">
            <div className={`bg-gradient-to-r ${expertiseAreas[activeTab].color} rounded-xl p-8 text-white mb-8`}>
              <div className="flex items-center space-x-4 mb-6">
                {expertiseAreas[activeTab].icon}
                <h4 className="text-2xl font-bold">{expertiseAreas[activeTab].title}</h4>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {expertiseAreas[activeTab].features.map((feature, index) => (
                <div 
                  key={index}
                  className={`bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-all duration-300 ${
                    animateCards ? 'animate-fade-in' : 'opacity-0'
                  }`}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                    <span className="text-gray-700 font-medium">{feature}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTS Case Study */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-12 text-white">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-3xl font-bold mb-6">Référence Compagnie des Transports Strasbourgeois</h3>
                <p className="text-lg mb-6 opacity-90">
                  Maintenance applicative complète de l'écosystème billettique : 
                  site web, applications mobiles, back-office agents, micro-services et batch.
                </p>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                    <span>6 lignes de tram + 45 lignes bus</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                    <span>Système critique 24/7</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                    <span>Innovation constante (Wizway, QR régional)</span>
                  </div>
                </div>
              </div>
              <div className="text-center">
                <div className="bg-white/20 rounded-xl p-8 backdrop-blur-sm">
                  <div className="text-4xl font-bold mb-2">80%</div>
                  <div className="text-lg">Réduction des problèmes techniques</div>
                  <div className="text-sm opacity-75 mt-2">depuis novembre 2024</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto text-center">
          <h3 className="text-3xl font-bold text-gray-900 mb-12">Technologies Maîtrisées</h3>
          <div className="flex flex-wrap justify-center gap-4">
            {technologies.map((tech, index) => (
              <span
                key={index}
                className="px-4 py-2 bg-white rounded-full shadow-md hover:shadow-lg transition-shadow duration-300 text-gray-700 font-medium"
              >
                {tech}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h3 className="text-4xl font-bold mb-6">Prêt à moderniser votre système ?</h3>
          <p className="text-xl mb-8 opacity-90">
            Bénéficiez de notre expertise éprouvée dans le transport public
          </p>
          <button className="bg-white text-blue-600 px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-colors duration-300 flex items-center space-x-2 mx-auto">
            <span>Contactez-nous</span>
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.6s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default ExpertiseBillettique;